// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// T12/T13 feature-screen copy (Today, Plan, Recipe, Pantry, Shop). Written
// fresh in the same plain, honest voice as the rest of the app — never
// implying data the API doesn't actually return (AGENTS.md).
export const viewsEn = {
    slots: {
        breakfast: "Breakfast",
        morning_snack: "Morning snack",
        lunch: "Lunch",
        afternoon_snack: "Afternoon snack",
        dinner: "Dinner",
        snack: "Snack",
    },
    today: {
        eatenEyebrow: "Eaten today",
        whyLabel: "Why?",
        nextEyebrow: "Next",
        // ADR-078: nothing later today is left unlogged — the leftover slot is the hero again, honestly labelled (never "Next", which would read as a prediction).
        stillOpenEyebrow: "Still open",
        // Calm Timeline (2026-09-23): the day-state header line — the authoritative
        // next-meal slot (pickTodayHero), never a client-computed "meals left" count.
        dayHeaderNext: "{{eaten}} kcal eaten · {{slot}} is next",
        dayHeaderStillOpen: "{{eaten}} kcal eaten · {{slot}} is still open",
        // Calm Timeline pixel-fidelity correction (2026-09-23d, artifact BXabQ2pJ3FsoRLKEWNQrzc): the compact
        // timeline row's eyebrow suffix for a logged-and-eaten meal, e.g. "BREAKFAST · DONE".
        timelineDone: "Done",
        allLoggedTitle: "Everything for today is logged.",
        allLoggedBody: "Tomorrow's meals are on your plan.",
        // P1 fix (founder-reported, 2026-09-23): the completion message is additional context, never a
        // replacement for today's calorie summary — the all-logged state shows this line too.
        allLoggedKcalSummary: "{{eaten}} / {{target}} kcal today",
        allLoggedKcalSummaryNoTarget: "{{eaten}} kcal today",
        projectedWord: "projected",
        targetWord: "target",
        plannedWord: "planned",
        thread: { dayLabel: "Today" },
        streakChip: "{{days}}-day streak",
        // ADR-080: a legacy "Milk / lactose" row waits for ONE non-blocking answer; the line stays until it is given.
        clarifyLine: "One question about your food rules: milk allergy or lactose intolerance?",
        clarifyLevelLine: "One question about your lactose intolerance: how strict is it?",
        clarifyCta: "Answer in Food & health",
        loggedChip: "{{done}}/{{planned}} meals logged this week",
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        subtitleDefault: "Here's what's on the plan for today.",
        subtitleReason: "Today's picks: {{reason}}.",
        noMealsTitle: "Nothing scheduled for today",
        noMealsBody: "Your current plan doesn't cover today's date. Open Plan to see the full week.",
        viewPlanCta: "View plan",
        targetBarLabel: "Daily target",
        ringEaten: "EATEN",
        ringPlanned: "PLANNED",
        heroProjected: "Projected today: {{projected}} / {{target}}",
        backfillTitle: "How did yesterday actually go?",
        // Calm Timeline pixel-fidelity correction (2026-09-23d): the resting banner's one trailing link.
        backfillReview: "Review",
        backfillBody: "{{count}} meals from yesterday are still unlogged.",
        backfillAllPlanned: "I had it all as planned",
        backfillChanged: "Something changed — tell Amy",
        backfillChangedPrompt: "Yesterday didn't go exactly as planned — here's what changed: ",
        backfillDismiss: "Leave it",
        backfillDone: "Yesterday is logged.",
        targetBarWithinRange: "Within your target range",
        ledgerEaten: "Eaten so far",
        ledgerProjected: "Projected today",
        ledgerFits: "{{kcal}} kcal of room left on top of your plan",
        ledgerOverProjection: "Projected {{delta}} kcal over target — ask Amy to rebalance",
        ledgerDinnerAtRisk: "Heads up: today is filling up early — dinner would be left very small.",
        ledgerLoggedTitle: "Logged off-plan",
        ledgerDeleteLabel: "Remove entry",
        targetBarOverRange: "Above your target range",
        targetBarUnderRange: "Below your target range",
        targetBarNoTarget: "No calorie target set",
        targetBarEatenTag: "eaten",
        targetBarPlannedToday: "Planned for today: {{kcal}}",
        // 2026-09-04 day state (server-owned): precedence ABOVE "room left".
        dayBelowFloorTitle: "Today is heading below your minimum",
        dayBelowFloorBody: "Right now today comes to about {{projected}} kcal, {{deficit}} kcal under your {{floor}} kcal minimum. Amy can help rebalance the rest of the day gently — no need to force anything.",
        dayBelowFloorCta: "Rebalance with Amy",
        dayBelowFloorPrompt: "Today is projected below my safe minimum — can you help me rebalance the rest of the day?",
        dayOnTarget: "Today is right on your target.",
        // Planned vs projected explanation (server projectionDelta).
        projectionLower: "Lower than your original plan ({{planned}} kcal) because {{reasons}}.",
        projectionHigher: "Higher than your original plan ({{planned}} kcal) because {{reasons}}.",
        projectionReasonPartial: "you ate part of {{slot}}",
        projectionReasonSkipped: "you didn't eat {{slot}}",
        projectionReasonChanged: "you changed {{slot}}",
        projectionReasonHad: "you ate a different amount of {{slot}}",
        projectionReasonOffPlan: "you logged food off-plan",
        projectionReasonJoin: " and ",
    },
    valueCard: {
        title: "Your value so far",
        realizedLabel: "Realized (from confirmed outcomes)",
        projectedLabel: "Projected (from this week's plan)",
        adherenceLabel: "{{recorded}} of {{planned}} meals logged",
        headlineClause: "Saved so far, from the {{recorded}} of {{planned}} meals you logged this week.",
        emptyTitle: "Add outcomes to see your progress",
        emptyBody: "Mark meals as had, partial, not eaten or changed and your realized savings will show up here. We never show €0 as a fact — only what's actually confirmed.",
        byKindHeading: "Where it came from",
        byKindPantryReuse: "From your kitchen",
        byKindOffer: "Retailer offers",
    },
    mealCard: {
        had: "Had it",
        other: "Other outcome",
        // ADR-078: "Not counted" read as "not yet processed" when it actually means "confirmed excluded, replaced" — renamed to say what it is.
        status: { had: "Logged", partial: "Logged · partly", skipped: "Not eaten", changed: "Changed" },
        macroLine: "Protein {{p}} g · Carbs {{c}} g · Fat {{f}} g",
        openRecipe: "Open recipe",
        // 2026-09-19: a changed meal opens no page — tapping it shows this note instead.
        changedNote: "You changed this meal.",
        swap: "Swap",
        lock: "Lock",
        unlock: "Unlock",
        markOutcome: "Mark outcome",
        moreActions: "Options",
        moreShort: "More",
        moreHint: "Swap the dish or mark the outcome",
        swapDish: "Swap dish",
        swapDishHint: "Shows other dishes that fit this slot",
        loggedAs: "Logged: {{outcome}}",
        partialDetail: "{{consumed}} of {{planned}} counted",
        // ADR-078: what actually replaced this slot — from the server-linked entry, never a day-wide guess.
        insteadOf: "Instead of {{meal}}",
        // ADR-078: the tap-to-log control on an unlogged meal-strip row.
        logCta: "Log",
        logOutcomeHint: "Record what happened",
        changeOutcome: "Change",
        lockedBadge: "Locked",
        batchBadge: "Batch",
        pantryBadge: "From your kitchen",
        activeTime: "{{minutes}} min hands-on",
        minutesShort: "{{minutes}} min",
        minutesActiveTotal: "{{active}} min active · {{total}} total",
        totalTime: "{{minutes}} min total",
        perServing: "per serving",
    },
    reasonBadges: {
        VEGAN_MATCH: "Vegan match",
        NUT_FREE: "Nut-free",
        TIME_FIT: "Fits your time budget",
        PANTRY_REUSE: "Uses what you have",
        LOWER_COST: "Lower cost",
        OFFER_MATCH: "On offer",
    },
    outcomeSheet: {
        howMuch: "How much of it?",
        notCounted: "Not counted",
        allOfIt: "All of it",
        counted: "will be counted",
        log: "Log it",
        title: "How did this meal go?",
        had: "Had it",
        partial: "Partly ate it",
        partialAmountQuestion: "How much of it did you eat?",
        skipped: "Not eaten",
        changed: "Changed it",
        submit: "Save outcome",
        submitting: "Saving…",
        success: "Outcome recorded.",
        error: "We couldn't record that outcome. This feature may not be available on this server yet — please try again later.",
        close: "Close",
        pantryUpdated: "Kitchen updated — {{count}} ingredient(s) deducted.",
        pantryRestored: "Kitchen updated — {{count}} ingredient(s) added back.",
        pantryHint: "Marking meals keeps your kitchen and shopping list accurate — BiteBalance plans around what you actually have.",
        pantryHintLink: "See your kitchen",
        changedWhatInstead: "What did you have instead?",
        changedLogWithAmy: "Log it with Amy",
        changedSkip: "No details — just mark it changed",
        changedBeta: "Beta — Amy estimates this together with you.",
        changedAmyPrefill: "I had {{food}} instead of {{meal}} ({{slot}}, {{date}}). Please log it.",
    },
    plan: {
        budgetConflictNotice: "This change takes the week about {{over}} over your {{budget}} weekly budget.",
        budgetConflictAccept: "Allow going over my budget for this week",
        budgetInfeasibleTitle: "No plan fits your budget yet",
        budgetInfeasibleBody: "We couldn't build a valid week within {{budget}} — the cheapest valid version is {{cheapest}} ({{gap}} over). Your current plan is unchanged. Ask Amy for options, or adjust the budget in Settings.",
        settingsDriftTitle: "This week was planned for {{planTarget}} kcal a day",
        settingsDriftBody: "Your daily target is now {{currentTarget}} kcal. You can keep following this week and change individual meals — any meal you change is matched to your new target. Or ask Amy to rework the week.",
        weekTitle: "Your week",
        // Fresh Adaptive Plan (ADR-068, mobile)
        mealsCount: "{{count}} meals",
        pricesEstimated: "≈ Prices are estimates.",
        mealsCountOne: "1 meal",
        goToDay: "Go to {{day}}",
        adaptedPill: "Adapted",
        adaptedAnnounce: "Plan adapted: {{day}}",
        generatingEyebrow: "Your week is taking shape",
        generatingTitle: "One moment — BiteBalance is planning around you.",
        viewDay: "Day",
        viewWeek: "Week",
        viewToggleLabel: "Plan layout",
        todayMarker: "Today",
        dailyMode: "Daily cooking",
        batchMode: "Batch cooking",
        hybridMode: "Mix both",
        lockedLabel: "Locked — kept during regeneration",
        batchSectionTitle: "Batch cooking this week",
        batchCardTitle: "Batch: {{title}}",
        portionsSupplied: "{{count}} portions, cooked once",
        // ADR-065 Prepared Food Inventory: cooked portions, never inferred from the plan.
        preparedSectionTitle: "Already cooked",
        preparedEmpty: "Nothing cooked ahead yet. Mark a batch as prepared once you have cooked it.",
        preparedPortionsLeft: "{{remaining}} of {{prepared}} portions left",
        preparedAllEaten: "All {{prepared}} portions eaten",
        preparedOn: "Cooked {{date}}",
        markPrepared: "Mark as prepared",
        markPreparedHint: "Confirm once you have actually cooked it. We take the ingredients out of your kitchen and keep the portions here.",
        markPreparedBusy: "Saving…",
        preparedKeepsNote: "Eating out, or a meal you don't eat from the plan, does not use a portion — it stays here until you eat it.",
        preparedUncovered: "Not everything was in your kitchen: {{items}} came from somewhere else, so nothing was deducted for it.",
        preparedError: "We couldn't save that. Please try again.",
        preparedPortionEaten: "{{remaining}} portions left",
        undoButton: "Undo last change",
        undoing: "Undoing…",
        undoSuccess: "Change undone. Your plan is back to how it was.",
        undoError: "We couldn't undo that change. Please reload and try again.",
        staleReloadTitle: "This plan changed elsewhere",
        staleReloadBody: "Someone (or something) updated your plan since this page loaded. Reload to see the current version before trying again.",
        reloadButton: "Reload",
        generating: "Generating your week — this takes a few seconds…",
        generateButton: "Generate my plan",
        noPlanYetBody: "We couldn't load a plan for you. If your first plan didn't generate (for example because a profile detail was missing), you can start it here.",
        warningsTitle: "Heads-up on this week's nutrition",
        warningMacroBand: "On {{count}} day(s), protein, carbs, or fat land outside the usual ±15% band. Calories and your safety limits are still on target — this is a nutrition note, not a safety issue.",
        warningNearBoundary: "Some days sit close to the edge of your daily target ranges. Small changes (like swapping a meal) could push them outside it.",
        warningGeneric: "Some days carry a nutrition note. Your safety checks all passed.",
        regenFailedTitle: "This plan may not match your latest settings",
        regenFailedBody: "Your last plan regeneration didn't succeed — your previous plan is still shown.",
        regenCta: "Regenerate plan",
        // P1 (founder-reported, 2026-09-22): a real plan whose week has ended —
        // distinct from "nothing scheduled" (no plan/revision ever existed).
        // Calm Timeline (2026-09-23): weekEndedBody refined to the founder's
        // exact approved subline; weekEndedTitle/Cta unchanged (already matched).
        weekEndedTitle: "Your week is complete",
        weekEndedBody: "Ready for the next one?",
        weekEndedCta: "Plan next 7 days",
        // Calm Timeline (2026-09-23): Today's week-ended state links to the plan
        // already on screen — the same plan/revision IS last week's until a new
        // one is generated, so this opens Plan rather than fetching new history.
        weekEndedViewLastWeek: "View last week",
        budgetInfeasibleAdjustCta: "Adjust budget",
        budgetInfeasibleKeepNote: "You can also keep your current plan and decide later.",
        conflictsTitle: "Your plan needs a small update",
        conflictsBodyOne: "1 meal includes a food you've chosen not to eat. We can replace it for you.",
        conflictsBodyOther: "{{count}} meals include foods you've chosen not to eat. We can replace them for you.",
        conflictsReplaceCta: "Update my plan",
        conflictsEditCta: "Edit food preferences",
        conflictsPreviewTitle: "Suggested replacements",
        conflictsApproveCta: "Use these meals",
        conflictsApplied: "Done — your plan and shopping list are updated.",
        conflictsError: "We couldn't prepare replacements just now. The affected meals stay marked so you can swap them yourself.",
        conflictsMealBadge: "Includes a food you avoid",
    },
    proposal: {
        title: "Review this change",
        changesTitle: "What would change",
        beforeLabel: "Before",
        afterLabel: "After",
        kcalDeltaLabel: "Calories",
        timeDeltaLabel: "Time",
        costDeltaLabel: "Cost",
        shoppingDeltaLabel: "Shopping list",
        approve: "Approve",
        approving: "Applying…",
        revise: "Show me another",
        lockChange: "{{title}} will be locked in place — swaps and rebuilds won't touch it.",
        unlockChange: "{{title}} will be unlocked — swaps and rebuilds may change it again.",
        lockApprove: "Lock it",
        unlockApprove: "Unlock it",
        timeDeltaHint: "hands-on cooking time",
        shoppingDeltaHint: "your shopping list total",
        noImpactHint: "No change to cooking time or your shopping list.",
        cancel: "Cancel",
        applySuccess: "Applied. Your plan has been updated.",
        tryAnother: "Try a different change",
        failTitle: "This change isn't safe to apply",
        loading: "Checking this change…",
        error: "We couldn't check this change. Please try again.",
        offerProvenance: "{{retailer}}: {{price}} until {{date}} — may vary by store (source: {{source}})",
        noCheaperFound: "Nothing cheaper found this week.",
        makeRoomInfeasible: "Even the lightest option for your other meals today can't make room for this portion.",
        noFitFound: "No smaller portion of this meal and no other option for this slot lands your day inside your range and above your minimum.",
        offerExpired: "This offer has ended — please request a fresh suggestion.",
        candidatesTitle: "Choose a replacement",
        candidatesIntro: "Dishes that fit this slot and your food rules, best match first.",
        candidatesNone: "No other dish fits this slot right now.",
        candidatesLoading: "Finding dishes…",
        candidatesChoose: "Review {{title}}",
        candidatesBack: "Other dishes",
        candidatesPlanned: "Planned",
        noFitWithinTime: "No other dish for this slot takes {{max}} minutes or less. The quickest one needs {{fastest}} minutes.",
        noFitWithinTimeNoFigure: "No other dish for this slot fits that time.",
    },
    validationReasons: {
        ALLERGEN_CONFLICT: "This would introduce an ingredient that conflicts with one of your allergies or intolerances.",
        HARD_EXCLUSION: "This would include something you've told us never to serve you.",
        MISSING_COMPOSITION: "We don't have complete ingredient data for this option yet, so we can't confirm it's safe.",
        NUTRITION_OUT_OF_RANGE: "This would push today's nutrition outside your target range.",
        TIME_LIMIT_EXCEEDED: "This would take longer than your available cooking time.",
        BATCH_YIELD_MISMATCH: "This would not produce the right number of batch portions.",
        generic: "This change didn't pass our safety and nutrition checks.",
    },
    recipe: {
        back: "Back to plan",
        // Release-readiness P1 (founder-reported, 2026-09-19): the outcome row's unlogged state, before the meal has any outcome at all.
        notYetLoggedLabel: "Not yet logged",
        servingsLabel: "Your portion",
        // Portion truth (2026-09-04): one portion on the whole page.
        plannedPortionLabel: "Your planned portion",
        batchPortionsFixed: "This meal is part of a batch cooked once for several dinners, so its portion can't be changed on its own.",
        servingsValue: "{{servings}} servings",
        servingsValueOne: "1 serving",
        portionNutritionLabel: "For your portion",
        baseServingLabel: "One recipe serving",
        recipeMakes: "Recipe as written makes {{count}} servings",
        ingredientsForPortion: "Ingredients for your portion ({{servings}} servings)",
        ingredientsForPortionOne: "Ingredients for your portion (1 serving)",
        stepperMin: "Smallest portion",
        stepperMax: "Largest portion",
        activeTimeLabel: "Active time",
        totalTimeLabel: "Total time",
        costLabel: "Estimated cost per serving",
        ingredientsTitle: "Ingredients",
        ingredientsGap: "A detailed ingredient list isn't available from the recipe service yet for this meal. This is a known gap in the current build, not a missing recipe — we show only real data here, never a placeholder ingredient list.",
        allergensTitle: "Allergens",
        allergensGap: "Sourced allergen data for this specific recipe isn't available from this view yet.",
        allergensDisclaimer: "Always check product labels — sourced data can be incomplete, and your safety comes first.",
        allergenResponsibility: "Allergen data comes from our sourced ingredient records. Product formulations and cross-contact vary — always check packaging and confirm each meal is safe for you before cooking or eating it.",
        yourAllergenFlag: "You declared this allergen",
        // ADR-080: level-aware — a compliant lactose-free meal is never flagged in the alarm tone
        yourIntoleranceFlagFreeOk: "Contains dairy — your plan only uses lactose-free dairy",
        yourIntoleranceFlagAvoidAll: "Your setting: no dairy",
        stepsTitle: "Steps",
        stepsGap: "Step-by-step instructions aren't available from the recipe service yet for this meal.",
        provenanceAmy: "Composed by Amy — grounded in our sourced ingredient data",
        recalcTitle: "Portions: {{from}} → {{to}}",
        recalcNote: "Nutrition and cost update with the portion size.",
        recalcRequest: "Updating…",
        recalcApplied: "This is a preview — nothing changes until you choose to use it.",
        recalcError: "We couldn't update the portion size. Please try again.",
        previewPendingTitle: "Updating to {{servings}}…",
        previewFailedTitle: "Couldn't update to {{servings}}",
        previewTitle: "Preview: {{servings}} servings",
        previewTitleOne: "Preview: 1 serving",
        previewIngredientsTitle: "Ingredients for {{servings}} servings (preview)",
        previewIngredientsTitleOne: "Ingredients for 1 serving (preview)",
        previewApply: "Use {{servings}} servings",
        previewApplyOne: "Use 1 serving",
        previewCancel: "Keep {{servings}} servings",
        previewCancelOne: "Keep 1 serving",
        previewApplying: "Saving…",
        previewApplied: "Your portion is now {{servings}} servings.",
        previewAppliedOne: "Your portion is now 1 serving.",
        previewApplyError: "We couldn't save this portion. Please try again.",
        previewStale: "Your plan changed in the meantime. Reload to continue.",
        previewMaxConflict: "This portion would push your next shop over your maximum checkout, so it can't be used.",
        blockedTitle: "This portion doesn't fit today's plan",
        blockedAbove: "It would bring today to about {{actual}} kcal. Your current range goes up to {{upper}} kcal.",
        blockedBelow: "It would bring today to about {{actual}} kcal. Your current range starts at {{lower}} kcal.",
        blockedOther: "It would break one of the rules your plan is built on, so it can't be used as it is.",
        blockedHint: "Try another portion size, or ask Amy to make room in today's plan.",
        blockedCta: "Ask Amy to make room",
        blockedPrompt: "Please make room in today's plan so I can have {{meal}} at {{servings}} servings — keep that meal as it is and adjust something else today.",
        blockedPromptOne: "Please make room in today's plan so I can have {{meal}} at 1 serving — keep that meal as it is and adjust something else today.",
        costUnavailable: "Cost estimate unavailable",
        notFoundTitle: "We couldn't find this meal",
        notFoundBody: "It may have been swapped or removed from your current plan.",
        // Fresh Adaptive Recipe (ADR-068 production implementation)
        heroActive: "{{minutes}} min hands-on",
        heroTotal: "{{minutes}} min total",
        stepperDecrease: "Fewer portions",
        stepperIncrease: "More portions",
        stepsCount: "{{count}} steps",
        stepsCountOne: "1 step",
        stepA11y: "Step {{n}}: {{text}}",
        macroKcal: "kcal",
        macroProtein: "protein",
        macroCarbs: "carbs",
        macroFat: "fat",
    },
    pantry: {
        title: "Your Kitchen",
        emptyHeroSubhead: "Amy plans around what you already have.",
        scanButton: "Scan a product",
        addManuallyButton: "Add manually",
        useSoon: "Use soon",
        scanInstructions: "Point your camera at the barcode",
        scanPermissionDeniedTitle: "Camera access needed",
        scanPermissionDeniedBody: "Allow camera access to scan a product, or add it manually instead.",
        scanPermissionSettingsButton: "Open Settings",
        scanNotFoundTitle: "Couldn't identify this product",
        scanNotFoundBody: "Add it manually instead — it only takes a moment.",
        scanErrorBody: "Something went wrong scanning that. Try again or add it manually.",
        scanFoundTitle: "Product found",
        scanTryAgain: "Try again",
        manualEntryTitle: "Add manually",
        manualModeStructured: "Quick add",
        manualModeFreeText: "Type a list",
        nameLabel: "Item",
        namePlaceholder: "e.g. Eggs",
        expiryOptionalLabel: "Expiry date (optional)",
        addExpiryButton: "Add expiry date",
        manualEntrySubmit: "Add",
        amyNoticedEyebrow: "Amy noticed",
        // The promise names the one action Amy can genuinely perform — a low-waste rebuild
        // of the remaining week through preview → approval — never a surgical swap that no
        // tool exists for. No plural engine in resolve.ts, so One/Many are explicit keys.
        amyNoticedDisplayOne: "You already have {{items}}. Want me to plan the rest of this week around it?",
        amyNoticedDisplayMany: "You already have {{items}}. Want me to plan the rest of this week around them?",
        // The handoff names the real Kitchen items, so Amy has scope — never a bare imperative.
        amyNoticedChatPrompt: "Plan the rest of this week around what's in my Kitchen: {{items}}.",
        askAmyKitchenPrompt: "Want help using what you already have?",
        optInTitle: "Track what's at home?",
        optInBody: "Tell BiteBalance what you already have and every plan builds around it — smaller shopping lists, less waste, lower bills.",
        optInBody2: "Type what you have — a short list is enough.",
        optInCta: "Turn on your kitchen",
        optInLater: "Maybe later",
        optInWorksNote: "Your weekly plan and shopping list work perfectly without it — this only makes them cheaper.",
        savedLine: "Your kitchen saved {{amount}} on this week's list",
        quantityLabel: "Quantity",
        uncertainPrefix: "about",
        expiryLabel: "Use by {{date}}",
        quickAddLabel: "Add items with text",
        quickAddPlaceholder: "e.g. 6 eggs and 300 g rice",
        extractButton: "Read items",
        extracting: "Reading…",
        candidatesTitle: "Confirm what we found",
        candidatesBody: "Check names, quantities and units before adding these to your kitchen.",
        matchedLabel: "Matched",
        unmatchedLabel: "Needs your confirmation — no catalog match",
        unmatchedSkipped: "{{count}} item(s) couldn't be matched to the catalog and weren't added — try a more specific name, e.g. 'white rice'.",
        didYouMean: "Did you mean:",
        notInCatalogTitle: "Not in the BiteBalance catalog yet",
        notInCatalogBody: "This item can't be added automatically.",
        backToKitchen: "Back to Kitchen",
        // Barcode state machine (founder QA 2026-09-14) — one line per user-visible state.
        uncertainTitle: "We think this is {{name}}",
        uncertainAsk: "Is that right?",
        uncertainConfirm: "Yes, that's right",
        notInCatalogFound: "We found \u201C{{product}}\u201D, but it isn't in the BiteBalance food catalog yet.",
        scanAnother: "Scan another product",
        quantityUnknownNote: "We couldn't read the pack size \u2014 check the amount.",
        quantityInvalid: "Enter an amount greater than 0.",
        lookupFailedTitle: "We couldn't look up this product",
        lookupFailedBody: "Check your connection and try again.",
        saveFailedTitle: "We couldn't save this product",
        confidenceLabel: "{{percent}}% confidence",
        confirmButton: "Add to kitchen",
        confirming: "Adding…",
        cancelButton: "Discard",
        confirmedSuccess: "Added to your kitchen.",
        confirmError: "We couldn't save these items. Please try again.",
        emptyTitle: "Your kitchen is empty",
        emptyBody: "Add what you have with the text box above, and BiteBalance will use it in your plan.",
        educationTitle: "Why your kitchen matters",
        educationBody: "Amy plans around what you already have, your shopping list automatically subtracts it, and marking meals as eaten keeps it up to date for you — less waste, and a smaller grocery bill.",
        educationDismiss: "Got it",
        educationShow: "Why does this matter?",
        editButton: "Edit",
        editQuantityLabel: "New quantity ({{unit}})",
        editSave: "Save amount",
        editSaving: "Saving…",
        editCancel: "Cancel",
        editError: "We couldn't update this item. Please try again.",
        removeButton: "Remove",
        removing: "Removing…",
        removeError: "We couldn't remove this item. Please try again.",
        updatedSuccess: "Kitchen updated.",
        headerEyebrow: "What you already have",
        headerTitle: "Your Kitchen",
        sectUseBy: "Use by",
        sectStock: "In stock",
        sectBought: "Just bought",
        sectCooked: "Cooked and waiting",
        useToday: "Use today",
        useTomorrow: "Use tomorrow",
        daysLeft: "{{count}} days",
        usesOne: "1 meal uses it",
        usesMany: "{{count}} meals use it",
        boughtSub: "From the shop · available now",
        expiringLabel: "Expiring",
        expiringOne: "expires {{when}} and is in one planned meal before then. Nothing to do.",
        expiringMany: "expires {{when}} and is in {{count}} planned meals before then. Nothing to do.",
        whenToday: "today",
        whenTomorrow: "tomorrow",
        rowHint: "Adjust the amount or remove",
        stepDownA11y: "{{step}} less",
        stepUpA11y: "{{step}} more",
        unitCount: "pcs",
    },
    shop: {
        budgetUnverifiable: "Most of this week is priced, but {{count}} item(s) have no reliable price yet — we can't confirm the plan is under your {{budget}} budget.",
        buyLine: "Buy {{qty}}",
        pantryCovers: "Your kitchen covers it — buy nothing",
        priceMix: "{{real}} real prices · {{est}} estimates",
        weekEyebrow: "Estimated",
        weekHeadline: "This week ≈ {{amount}}",
        weekHeadlineBudget: "This week ≈ {{amount}} · Budget {{budget}}",
        weekHeadlineOverBudget: "This week ≈ {{amount}} · {{delta}} over budget",
        weekCheckoutLine: "Whole-pack shop ≈ {{amount}} · leftovers stay in your Kitchen",
        budgetOver: "≈ {{over}} over your {{budget}} weekly budget",
        budgetTrim: "Ask Amy to trim",
        detailsA11y: "Show the math for this item",
        tabPantry: "Kitchen",
        tabShopping: "Shopping list",
        pantryLink: "Looking for your kitchen? Manage it on the Kitchen page.",
        requiredLabel: "Required",
        reservedLabel: "From your kitchen",
        netLabel: "Net needed",
        packLabel: "Pack",
        buyLabel: "Buy",
        leftoverLabel: "Leftover",
        sourceMealsLabel: "Used in {{count}} meals",
        // WS3 Shop → Pantry v1: per-row purchase confirmation + "Shop done".
        purchaseTitle: "What came home?",
        purchaseHint: "Tell us per item — only what you confirm goes into your Kitchen. Forecast leftovers never do.",
        purchaseBought: "Bought",
        purchaseAlreadyHad: "Already had",
        purchaseNotBought: "Not bought",
        purchaseStateBought: "Bought · {{qty}} in your Kitchen",
        purchaseStateAlreadyHad: "Already had · {{qty}} added to your Kitchen",
        purchaseStateAlreadyHadNone: "Already had — nothing added to your Kitchen",
        purchaseStateNotBought: "Not bought — nothing in your Kitchen",
        purchaseStateA11y: "Purchase state for {{name}}",
        purchaseQtyLabel: "Quantity that came home ({{unit}})",
        purchaseQtySave: "Update quantity",
        shopDoneButton: "Shop done",
        shopDoneHint: "Marks every open item as bought at its listed amount. You can correct any item afterwards.",
        shopDoneAllResolved: "Every item is confirmed.",
        purchaseSynced: "Kitchen updated: {{added}} item(s) added, {{adjusted}} corrected.",
        purchaseSyncedNone: "Saved — nothing changed in your Kitchen.",
        purchaseError: "We couldn't save your purchase. Please try again.",
        manualBadge: "Added by you",
        listTotalLabel: "Estimated total",
        addManualTitle: "Add an item",
        addManualName: "Item name",
        addManualQty: "Quantity (optional)",
        addManualButton: "Add item",
        exportButton: "Copy list",
        exportCopied: "List copied — paste it into your retailer's app.",
        exportShared: "List shared.",
        exportRetailerLine: "Works with REWE, EDEKA, Lidl and more — paste into any grocery app or list.",
        emptyTitle: "Your shopping list is empty",
        emptyBody: "Once you have a plan, everything you need to buy will show up here.",
        offerBadge: "Offer",
        offerChipValidUntil: "until {{date}}",
        offerChipAria: "Offer at {{retailer}}: {{price}}, valid until {{date}} — may vary by store",
        offerPackagePriceLabel: "Offer price (per package, not per serving): {{price}}",
        offerUnitPrice: "{{price}} per {{unit}}",
        offerSourceLine: "Source: {{source}}, captured {{date}}",
        offerMayVary: "May vary by store",
        unpricedNote: "{{count}} item(s) unpriced",
        // T21 propagation 2026-09-10: the list is the object; the accounting keeps every word it had, one tap away.
        costDisclosure: "Cost and budget",
        costDisclosureHint: "The estimate for this list, the checkout verdict and your weekly goal",
        priceUnavailable: "price unavailable",
        realPriceBadge: "Current",
        // Fresh Adaptive Shop (ADR-068): the precise list's title, its derived groups and the Shop → Kitchen thread.
        screenTitle: "Shopping",
        groupToBuy: "To buy",
        groupCovered: "Your kitchen covers it",
        allTickedLabel: "Everything bought",
        kitchenUpdatedLabel: "Kitchen up to date",
        toKitchen: "To the kitchen",
        // Shop list clarity (2026-10-06): the need beside the pack bought, the supermarket sections, the words for a counted unit and a pack.
        needLine: "Need {{need}}",
        needLineRemaining: "Still need {{need}}",
        decimalMark: ".",
        section: {
            produce: "Fruit & veg",
            chilled: "Chilled",
            meat_fish: "Meat & fish",
            frozen: "Frozen",
            dry_goods: "Dry goods & staples",
            spices_oils: "Spices & oils",
            drinks: "Drinks",
            other: "Other",
        },
        countNoun: { pieceOne: "piece", pieceMany: "pieces", cloveOne: "clove", cloveMany: "cloves" },
        packNoun: { bulbOne: "bulb", bulbMany: "bulbs", netOne: "net", netMany: "nets", packOne: "pack", packMany: "packs" },
    },
    // Budget Model v2 (ADR-052). Metric NAMES and definitions come from the
    // canonical glossary module (glossary.ts) — never re-typed here. These are
    // the surrounding UI strings only.
    budget: {
        spendingDisclosure: "Your food spending",
        sectionTitle: "Grocery budget",
        perWeek: "/ week",
        optional: "Optional",
        remove: "Remove",
        noGoalSet: "No spending goal set",
        noMaxSet: "No maximum set",
        invalidAmount: "Enter a valid amount, e.g. 45 or 45,50.",
        saved: "Budget saved.",
        outlookTitle: "Your budget outlook",
        outlookBasis: "Estimate, from this week's plan — not actual spending.",
        weeklyGoalRow: "Weekly goal",
        differenceRow: "Difference",
        notTrackedYet: "Not tracked yet",
        notTrackedHint: "BiteBalance doesn't record what you pay yet. Recording real shops is planned for a future update.",
        cantConfirm: "Can't confirm yet — {{count}} item(s) without a reliable price",
        noPlanYet: "Your outlook appears once you have a plan.",
        // Migration SETUP card (redesign 2026-09-04): configure the DESIRED
        // state directly (goal prefilled with the previous amount, editable;
        // optional single-shop maximum; "no goal" for maximum-only). Never
        // "legacy", "hard", or internal names. Works for any previous amount.
        setupTitle: "Set up your grocery budget",
        setupBody: "You previously set a {{amount}} weekly budget. BiteBalance now separates your average weekly spending goal from an optional limit on a single grocery shop.",
        setupGoalHelper: "How much you'd like to spend on groceries per week on average.",
        setupNoGoal: "I don't want a weekly spending goal",
        setupMaxToggle: "Set a maximum for one shop",
        setupMaxHelper: "The most you want to spend in a single grocery shop.",
        setupSave: "Save budget",
        setupNothingChosen: "Choose a weekly spending goal, a maximum for one shop, or both.",
        setupSaved: "Your budget is set up.",
        legacyLater: "Decide later",
        // Persistent compact notice while the account is still unclassified.
        pendingNoticeTitle: "Your previous {{amount}} budget is still limiting each shop.",
        pendingNoticeBody: "Set up your new budget options whenever you're ready.",
        pendingNoticeCta: "Set up",
        previousBudgetLabel: "Your previous budget · {{amount}}/week",
        previousBudgetHint: "Until you update this setting, BiteBalance will continue treating {{amount}} as a limit for each grocery shop.",
        // Today / Plan while unclassified: explain the transition instead of a
        // "your maximum" failure the user never chose.
        todayTransitionTitle: "Choose how your {{amount}} budget should work",
        todayTransitionBody: "Your existing {{amount}} budget is still limiting each shop until you set up your new weekly spending goal and optional checkout maximum.",
        todayTransitionCta: "Set up budget",
        // Applicability (owner invariant 2026-09-04): a failure produced under
        // EARLIER settings is history, never a current "your maximum".
        staleSettingsTitle: "Your plan was built with earlier settings",
        staleSettingsBody: "Your budget settings have changed since this plan was built. Rebuild when you're ready to update the week.",
        staleSettingsCta: "Rebuild plan",
        // Failed-candidate wording: an alternative found during an unsuccessful
        // rebuild — never the current shopping list.
        candidateNote: "This is the lowest-cost alternative BiteBalance found during that rebuild — it is not your current shopping list.",
        // Shop completeness (ceiling-independent): a partial basket is never a total.
        pricedSoFar: "Priced so far",
        needsPriceCount: "{{count}} item(s) still need a reliable price",
        // Shop headline (2026-10-06): the figure is the checkout price of WHOLE PACKS, not the value of the food eaten. Founder wording
        // (2026-10-06): the COMPLETE estimate may say "total"; a partial one never does ("Priced so far") — the narrowed pin in applicability-shop-completeness.
        completeEstimateLabel: "Estimated total · whole packs",
        liveEstimateLabel: "Estimate with today's prices and kitchen",
        liveEstimateNote: "Recalculated from your current kitchen and current prices — it can differ from the list below.",
        partialEconomicsNote: "The food-used and carryover split appears once every item has a reliable price.",
        shopCheckoutTitle: "This shop",
        shopWithinMax: "≈ {{cost}} — {{under}} under your {{max}} maximum",
        shopExceedsMax: "≈ {{cost}} — {{over}} over your {{max}} maximum. Change or remove the maximum in Settings.",
        shopUnverifiableMax: "{{count}} item(s) have no reliable price yet — we can't confirm this shop fits under your {{max}} maximum.",
        shopGoalLine: "Forecast weekly average ≈ {{forecast}} (estimate) · goal {{goal}} · {{status}}",
        shopGoalUnavailable: "Forecast weekly average not available — {{reason}}",
        shopLegacyLimitNote: "Your previous {{amount}} budget is still limiting each shop. Set up your new budget options in Settings.",
        todayLine: "Forecast ≈ {{forecast}}/week · {{status}}",
        insightsForecastTitle: "Forecast weekly average over time",
        insightsForecastNote: "Each point is an estimate from that plan version — not what you spent.",
        insightsActualTitle: "Actual weekly average",
        insightsActualCta: "Record what you spent after a shop — coming in a future update.",
        planMaxInfeasibleTitle: "No valid week fits under your maximum",
        planMaxInfeasibleBody: "We couldn't build a week whose shop stays under {{max}} — the lowest-cost plan we found costs {{cheapest}} at the till ({{over}} over). Your current plan is unchanged. Raise or remove the maximum in Settings, or ask Amy for options.",
        planLegacyInfeasibleBody: "Your previous {{max}} budget is still limiting each shop — the lowest-cost plan we found costs {{cheapest}} at the till ({{over}} over). Your current plan is unchanged. Set up your new weekly spending goal and optional checkout maximum in Settings.",
        planMaxInfeasibleCta: "Open budget settings",
        planGoalImpact: "Forecast weekly average {{before}} → {{after}} · {{status}}",
        planCheckoutConflict: "This change takes this week's shop about {{over}} over your {{max}} maximum, so it can't be applied. Change or remove the maximum in Settings.",
        onboardingGoalLabel: "Weekly spending goal (optional)",
        onboardingGoalHint: "The amount you'd like your groceries to average per week. You can set a maximum per shop later in Settings.",
        onboardingSkip: "Skip for now",
        guideCenterTitle: "Guide Center",
        guideCenterGuides: "Guides",
        guideCenterGuidesSub: "Practical reads on food, weight and habits",
        guideCenterGlossary: "Glossary",
        guideCenterGlossarySub: "What every BiteBalance number means",
        guideCenterBudgetGuide: "How your grocery budget works",
        guideCenterBudgetGuideSub: "Your weekly spending goal, the next shop and what carries over",
        budgetGuideLinkText: "How your grocery budget works",
        glossaryHubCardTitle: "Glossary",
        glossaryHubCardSubtitle: "The words BiteBalance uses for your budget, food and progress — explained in plain language.",
        infoAria: "What does {{name}} mean?",
    },
};
